import { Component, computed, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { fb } from '../../../../shared/shared.global-service';
import { Validators } from '@angular/forms';
import { FileSelectEvent, FileUploadEvent } from '@openng/optimus-ui/fileupload';
import { InputFileBtnComponent } from '../../common/input-file-btn/input-file-btn.component';
import { FileObject, UserGridItemObject } from '@it2go/types';
import { UserExperienceDialogStore } from './user-experience-dialog.store';
import { UserExperienceObject } from '@libs/types/src/user/object-type/user-experience.object';
import { isInvalid } from '../../../../shared/helpers/validate';
import { UserExperienceCreateInput } from '@libs/types/src/user/input-type/user-experience.create-input';
import { UserExperienceUpdateInput } from '@libs/types/src/user/input-type/user-experience.update-input';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import { FileLinkComponent } from '../../../../shared/components/kit/file-link/file-link.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { NumberInputComponent } from '../../../../../components/kit/input/text-input/number-input.component';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { EducationGqlService } from '../../../../../services/education.gql-service';
import { EmploymentTypeEducationGqlService } from '../../../../../services/employment-type-education.gql-service';
import { EmploymentTypeEducationObject } from '@libs/types/src/services/object-type/employment-type-education.object';
import { EducationObject } from '@libs/types/src/services/object-type/education.object';
import { FilterBuilder } from '../../../helper/filter.builder';

interface EducationSelectOption {
    id: number;
    educationId: number;
    mpsvId: number;
    name: string;
    description: string;
}

interface EducationSelectGroup {
    name: string;
    employmentType: string;
    items: EducationSelectOption[];
}

@Component({
    selector: 'app-user-experience-dialog',
    templateUrl: './user-experience-dialog.component.html',
    styleUrl: './user-experience-dialog.component.scss',
    providers: [UserExperienceDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FileLinkComponent,
        InputFileBtnComponent,
        DateInputComponent,
        TranslatePipe,
        TextInputComponent,
        NumberInputComponent,
        CheckInputComponent,
        SelectInputComponent,
    ],
})
export class UserExperienceDialogComponent
    extends BaseDialogComponent
    implements OnInit {
    protected readonly store = inject(UserExperienceDialogStore);
    private readonly educationService = inject(EducationGqlService);
    private readonly employmentTypeEducationService = inject(
        EmploymentTypeEducationGqlService,
    );
    private readonly translate = inject(TranslateService);

    @ViewChild('files') files!: InputFileBtnComponent;

    protected form = fb.group({
        date: fb.control<Date | null>(null, [Validators.required]),
        hours: fb.control<number>(0, [Validators.required]),
        name: fb.control('', [Validators.required]),
        countInEducation: fb.control(false),
        employmentTypeEducationId: fb.control<number | null>(null),
    });
    protected fileName = '';
    protected readonly countInEducation = signal(false);
    protected readonly selectedDate = signal<Date | null>(null);
    protected readonly educationGroups = signal<EducationSelectGroup[]>([]);
    protected readonly educationOptionsLoading = signal(false);
    protected readonly educationHelpText = computed(() => {
        if (!this.selectedDate()) {
            return 'user.experience.fields.educationSelectDateFirst';
        }
        if (!this.educationOptionsLoading() && !this.educationGroups().length) {
            return 'user.experience.fields.educationUnavailable';
        }
        return 'user.experience.fields.educationTypeHelp';
    });

    private employmentTypeEducationItems: EmploymentTypeEducationObject[] = [];
    private educationById = new Map<number, EducationObject>();
    private educationOptionsLoaded = false;

    selectedFiles: File[] = [];
    user: UserGridItemObject | null = null;
    experience: UserExperienceObject | null = null;
    profile = false;

    constructor() {
        super();
        this.store.registerSuccessMessage(
            'createUserExperience',
            'common.created',
        );
        this.store.registerSuccessMessage(
            'updateUserExperience',
            'common.updated',
        );
        this.store.registerSuccessMessage(
            'deleteUserExperience',
            'common.deleted',
        );
        this.store.doOnActions(
            () => this.dialogRef.close(),
            'deleteUserExperience',
            'createUserExperience',
            'updateUserExperience',
        );
    }

    ngOnInit(): void {
        this.user = this.dialogConfig.data.user;
        this.experience = this.dialogConfig.data.experience;
        this.profile = this.dialogConfig.data.profile;
        if (this.experience) {
            this.form.patchValue({
                ...this.experience,
                date: new Date(this.experience.date),
            });
            this.fileName = `${this.user?.surname}-${this.experience.name}`;
        }
        this.countInEducation.set(
            this.form.controls.countInEducation.value ?? false,
        );
        this.selectedDate.set(this.form.controls.date.value);
        this.updateEducationValidator(this.countInEducation());
        this.subs.push(
            this.form.controls.name.valueChanges.subscribe((value) => {
                this.fileName = `${this.user?.surname}-${value}`;
            }),
            this.form.controls.countInEducation.valueChanges.subscribe(
                (value) => {
                    const countInEducation = value ?? false;
                    this.countInEducation.set(countInEducation);
                    this.updateEducationValidator(countInEducation);
                    this.refreshEducationGroups();
                },
            ),
            this.form.controls.date.valueChanges.subscribe((value) => {
                this.selectedDate.set(value);
                this.refreshEducationGroups();
            }),
        );
        void this.loadEducationOptions();
    }

    override async save() {
        const userId = this.user?.id;
        if (isInvalid(this.form) || !userId) return;

        let result: FileObject | null = null;
        if (this.selectedFiles.length) {
            result = await this.files.saveFile({
                files: this.selectedFiles,
            } as FileUploadEvent);
        }

        if (!this.experience) {
            const data = {
                userId,
                ...this.form.value,
                hours: Number(this.form.value.hours),
                file: result?.path,
            } as UserExperienceCreateInput;
            this.store.api.createUserExperience({
                input: data,
                profile: this.profile,
            });
        } else {
            const data = {
                userId,
                id: this.experience.id,
                ...this.form.value,
                file: result?.path || this.experience.file,
                hours: Number(this.form.value.hours),
            } as UserExperienceUpdateInput;
            this.store.api.updateUserExperience({
                input: data,
                profile: this.profile,
            });
        }
    }

    onSelect(event: FileSelectEvent): void {
        this.selectedFiles = event.currentFiles;
    }

    private async loadEducationOptions(): Promise<void> {
        const employmentTypes = [
            ...new Set(
                (this.user?.employments ?? [])
                    .map((employment) => employment.employmentTypeName)
                    .filter((value): value is string => Boolean(value)),
            ),
        ];

        if (!employmentTypes.length) {
            this.educationOptionsLoaded = true;
            this.refreshEducationGroups();
            return;
        }

        this.educationOptionsLoading.set(true);
        try {
            const [mappingResult, educationResult] = await Promise.all([
                this.employmentTypeEducationService.getEmploymentTypeEducations(
                    new FilterBuilder()
                        .where('employmentType', employmentTypes)
                        .sort('employmentType')
                        .sort('educationId')
                        .limit(1_000)
                        .filter(),
                ),
                this.educationService.getEducations(
                    new FilterBuilder().sort('mpsvId').limit(1_000).filter(),
                ),
            ]);

            this.employmentTypeEducationItems = mappingResult.items ?? [];
            this.educationById = new Map(
                (educationResult.items ?? []).map(
                    (education: EducationObject) => [education.id, education],
                ),
            );
            this.educationOptionsLoaded = true;
            this.refreshEducationGroups();
        } finally {
            this.educationOptionsLoading.set(false);
        }
    }

    private refreshEducationGroups(): void {
        const date = this.selectedDate();
        if (!date) {
            this.educationGroups.set([]);
            return;
        }

        const activeEmploymentTypes = new Set(
            (this.user?.employments ?? [])
                .filter((employment) =>
                    this.isEmploymentActiveAtDate(
                        employment.dateFrom,
                        employment.dateTo,
                        date,
                    ),
                )
                .map((employment) => employment.employmentTypeName)
                .filter((value): value is string => Boolean(value)),
        );
        const groups = new Map<string, EducationSelectGroup>();

        for (const mapping of this.employmentTypeEducationItems) {
            if (!activeEmploymentTypes.has(mapping.employmentType)) continue;

            const education = this.educationById.get(mapping.educationId);
            if (!education) continue;

            if (!groups.has(mapping.employmentType)) {
                groups.set(mapping.employmentType, {
                    name: this.translate.instant(
                        `user.common.employmentTypes.${mapping.employmentType}`,
                    ),
                    employmentType: mapping.employmentType,
                    items: [],
                });
            }
            groups.get(mapping.employmentType)!.items.push({
                id: mapping.id,
                educationId: education.id,
                mpsvId: education.mpsvId,
                name: education.name,
                description: education.description,
            });
        }

        const options = [...groups.values()]
            .map((group) => ({
                ...group,
                items: group.items.sort((a, b) => a.mpsvId - b.mpsvId),
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
        this.educationGroups.set(options);

        if (!this.educationOptionsLoaded) return;

        const selectedId = this.form.controls.employmentTypeEducationId.value;
        const selectedIsAvailable = options.some((group) =>
            group.items.some((item) => item.id === selectedId),
        );
        if (selectedId !== null && !selectedIsAvailable) {
            this.form.controls.employmentTypeEducationId.setValue(null);
        }
    }

    private updateEducationValidator(countInEducation: boolean): void {
        const control = this.form.controls.employmentTypeEducationId;
        if (countInEducation) {
            control.addValidators(Validators.required);
        } else {
            control.clearValidators();
            control.setValue(null);
        }
        control.updateValueAndValidity();
    }

    private isEmploymentActiveAtDate(
        dateFrom: string | null,
        dateTo: string | null,
        date: Date,
    ): boolean {
        const dateValue = date.getTime();
        return (
            (!dateFrom || new Date(dateFrom).getTime() <= dateValue) &&
            (!dateTo || new Date(dateTo).getTime() >= dateValue)
        );
    }

    protected readonly DefaultCategoryTypeEnum = DefaultCategoryTypeEnum;
}
